/**
 * A thin `fetch`-stream Server-Sent-Events reader — deliberately NOT the AI-SDK
 * React UI hooks (the client holds zero AI logic; it renders a VIEW onto a
 * detached run). The edge writes each run event as an SSE frame
 * (`event: <type>\ndata: <json>\n\n`); this module turns the response's byte
 * stream into ordered {@link ChatEvent}s.
 *
 * Split into a pure, stateful {@link SseFrameParser} (bytes/strings → frames,
 * unit-testable with arbitrary chunk boundaries) and the {@link streamSse} /
 * {@link streamChatEvents} async generators that drive it over a
 * `ReadableStream`. Because the parser is fed decoded strings, a multi-byte
 * character or a frame split across two network chunks is reassembled correctly.
 */
import { type ChatEvent, parseChatEvent } from './run-events.ts';

/** One dispatched SSE frame: the `event:` field (default `'message'`) + joined `data:`. */
export interface ServerSentEvent {
  readonly event: string;
  readonly data: string;
  /** The `id:` field when present — unused by us but part of the format. */
  readonly id?: string;
}

const DEFAULT_EVENT = 'message';

/**
 * Incremental SSE parser. Feed it decoded text with {@link push}; it returns any
 * frames completed by that text (a blank line dispatches a frame). Call
 * {@link flush} at end-of-stream to drain a trailing frame with no final blank
 * line. Holds only the in-progress line + the current frame's fields.
 */
export class SseFrameParser {
  private buffer = '';
  private eventType = DEFAULT_EVENT;
  private dataLines: string[] = [];
  private lastId: string | undefined;
  private sawField = false;

  /** Feed a chunk of decoded text; returns every frame it completed, in order. */
  push(chunk: string): ServerSentEvent[] {
    this.buffer += chunk;
    const out: ServerSentEvent[] = [];
    let newlineIdx = this.indexOfLineEnd();
    while (newlineIdx.index !== -1) {
      const line = this.buffer.slice(0, newlineIdx.index);
      this.buffer = this.buffer.slice(newlineIdx.index + newlineIdx.length);
      const frame = this.consumeLine(line);
      if (frame) {
        out.push(frame);
      }
      newlineIdx = this.indexOfLineEnd();
    }
    return out;
  }

  /** Dispatch a final frame if the stream ended without a trailing blank line. */
  flush(): ServerSentEvent[] {
    // A leftover partial line (no terminator) is still a field per the buffer.
    if (this.buffer.length > 0) {
      const line = this.buffer;
      this.buffer = '';
      this.consumeLine(line);
    }
    const frame = this.dispatch();
    return frame ? [frame] : [];
  }

  /** Locate the next line terminator (`\n`, `\r\n`, or lone `\r`) in the buffer. */
  private indexOfLineEnd(): { index: number; length: number } {
    for (let i = 0; i < this.buffer.length; i += 1) {
      const ch = this.buffer[i];
      if (ch === '\n') {
        return { index: i, length: 1 };
      }
      if (ch === '\r') {
        // `\r\n` is one terminator; a lone `\r` (not last char) is also one.
        if (this.buffer[i + 1] === '\n') {
          return { index: i, length: 2 };
        }
        if (i < this.buffer.length - 1) {
          return { index: i, length: 1 };
        }
        // Trailing `\r` — wait for the next chunk to know if it is `\r\n`.
        return { index: -1, length: 0 };
      }
    }
    return { index: -1, length: 0 };
  }

  /** Process one complete line; a blank line dispatches the buffered frame. */
  private consumeLine(line: string): ServerSentEvent | null {
    if (line === '') {
      return this.dispatch();
    }
    if (line.startsWith(':')) {
      // A comment/keep-alive line — ignored, but it does not reset the frame.
      return null;
    }
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    // A single leading space after the colon is stripped per the SSE spec.
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) {
      value = value.slice(1);
    }
    this.applyField(field, value);
    return null;
  }

  private applyField(field: string, value: string): void {
    switch (field) {
      case 'event':
        this.eventType = value;
        this.sawField = true;
        break;
      case 'data':
        this.dataLines.push(value);
        this.sawField = true;
        break;
      case 'id':
        this.lastId = value;
        this.sawField = true;
        break;
      default:
        // `retry` and unknown fields are accepted but not surfaced.
        break;
    }
  }

  /** Emit the current frame (if any field was set) and reset for the next. */
  private dispatch(): ServerSentEvent | null {
    if (!this.sawField) {
      return null;
    }
    const frame: ServerSentEvent = {
      event: this.eventType,
      data: this.dataLines.join('\n'),
      ...(this.lastId !== undefined ? { id: this.lastId } : {}),
    };
    this.eventType = DEFAULT_EVENT;
    this.dataLines = [];
    this.sawField = false;
    // `lastId` persists across frames per the SSE spec (last-event-id).
    return frame;
  }
}

/** Read a byte stream as ordered {@link ServerSentEvent} frames. */
export async function* streamSse(
  body: ReadableStream<Uint8Array>,
  signal?: AbortSignal,
): AsyncGenerator<ServerSentEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const parser = new SseFrameParser();
  try {
    while (true) {
      if (signal?.aborted) {
        return;
      }
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      // `stream: true` keeps a partial multi-byte character for the next chunk.
      for (const frame of parser.push(decoder.decode(value, { stream: true }))) {
        yield frame;
      }
    }
    for (const frame of parser.flush()) {
      yield frame;
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Read a run's SSE body as validated {@link ChatEvent}s in order. Frames whose
 * `data` is empty (keep-alives) or corrupt are skipped — the view keeps tailing.
 */
export async function* streamChatEvents(
  body: ReadableStream<Uint8Array>,
  signal?: AbortSignal,
): AsyncGenerator<ChatEvent> {
  for await (const frame of streamSse(body, signal)) {
    if (frame.data === '') {
      continue;
    }
    const event = parseChatEvent(frame.data);
    if (event) {
      yield event;
    }
  }
}
